import { ChevronDown, ChevronUp, Users, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useAdminEventBookings, AdminEventBooking } from "@/hooks/useAdminEventBookings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatGroupTime } from "@/lib/eventGroups";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/types/event";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface AdminEventBookingsProps {
  event: Event;
}

const AdminEventBookings = ({ event }: AdminEventBookingsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: bookings, isLoading, error } = useAdminEventBookings(isExpanded ? event.id : null);

  const { data: interestList } = useQuery({
    queryKey: ['admin-event-interest', event.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_interest')
        .select('id, created_at, notified_at, profiles!user_id(first_name, last_name, email)')
        .eq('event_id', event.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: isExpanded,
  });

  const queryClient = useQueryClient();
  const { data: groups } = useQuery({
    queryKey: ['admin-event-groups', event.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_groups')
        .select('id, name, start_time, capacity, display_order')
        .eq('event_id', event.id)
        .order('start_time', { ascending: true })
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: isExpanded,
  });
  const tz = event.timezone || 'Europe/London';
  const bookedInGroup = (groupId: string) =>
    (bookings ?? []).filter((b) => b.group_id === groupId).reduce((s, b) => s + b.quantity, 0);

  // Admin-only move; the RPC takes the event lock then the wave lock and refuses a full wave.
  // When the ticket actually moved, the guest gets a departure-change email (awaited, not
  // fire-and-forget) so the admin knows if they must message the guest directly.
  const reassign = useMutation({
    mutationFn: async ({ ticketId, groupId, previousGroupId }: { ticketId: string; groupId: string; previousGroupId: string | null }) => {
      const { data, error } = await supabase.rpc('reassign_ticket_group', { _ticket_id: ticketId, _group_id: groupId });
      if (error) throw error;
      const moved = !!(data as { moved?: boolean } | null)?.moved;
      if (!moved) return { moved, notified: false };
      const { error: notifyError } = await supabase.functions.invoke('send-departure-change', {
        body: { ticketId, previousGroupId },
      });
      if (notifyError) console.error('Departure change email failed:', notifyError);
      return { moved, notified: !notifyError };
    },
    onSuccess: ({ moved, notified }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-event-bookings', event.id] });
      if (!moved) return;
      if (notified) {
        toast.success('Attendee moved and emailed their new departure time');
      } else {
        toast.error('Attendee moved, but the guest was NOT notified — please message them directly', { duration: 10000 });
      }
    },
    onError: (err: Error) => toast.error(err.message || 'Could not move attendee'),
  });

  const totalBooked = event.booked || 0;
  const capacity = event.capacity;
  const spotsAvailable = capacity - totalBooked;

  const formatBookingHistory = (count: number) => {
    if (count <= 1) return "First-timer";
    return `${count} past events`;
  };

  const formatFullName = (booking: AdminEventBooking) => {
    const firstName = booking.profiles?.first_name || "";
    const lastName = booking.profiles?.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Unknown";
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-muted"
      >
        <div className="flex items-center gap-3">
          <Users className="w-4 h-4" />
          <span className="font-medium">Latest Booking Info</span>
          <span className="text-sm text-muted-foreground">
            {totalBooked}/{capacity} tickets booked ({spotsAvailable} spots available)
          </span>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {isExpanded && (
        <div className="border-t p-4">
          <div className="mb-4">
            <h4 className="font-semibold mb-2">Booking Progress</h4>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${(totalBooked / capacity) * 100}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground">
                {totalBooked}/{capacity}
              </span>
            </div>
          </div>

          {groups && groups.length > 0 && (
            <div className="mb-4 space-y-2">
              <h4 className="font-semibold">By wave</h4>
              {groups.map((g) => {
                const b = bookedInGroup(g.id);
                return (
                  <div key={g.id} className="flex items-center gap-2 text-sm">
                    <span className="w-40 truncate">{g.name} · {formatGroupTime(g.start_time, tz)}</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: `${g.capacity ? Math.min(100, (b / g.capacity) * 100) : 0}%` }} />
                    </div>
                    <span className="text-muted-foreground">{b}/{g.capacity}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <h4 className="font-semibold mb-3">Guest List</h4>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-destructive text-sm py-4">
                Error loading bookings: {error.message}
              </div>
            ) : !bookings || bookings.length === 0 ? (
              <div className="text-muted-foreground text-sm py-4">
                No confirmed bookings yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Booking History</TableHead>
                    <TableHead>Coupon Used</TableHead>
                    {groups && groups.length > 0 && <TableHead>Wave</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="font-medium">
                        {formatFullName(booking)}
                        {booking.quantity > 1 && (
                          <span className="text-sm text-muted-foreground ml-2">
                            (+{booking.quantity - 1})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{booking.profiles?.email || "N/A"}</TableCell>
                      <TableCell>{booking.profiles?.phone || "N/A"}</TableCell>
                      <TableCell>{formatBookingHistory(booking.booking_count)}</TableCell>
                      <TableCell>
                        {booking.coupon_code || <span className="text-muted-foreground">None</span>}
                      </TableCell>
                      {groups && groups.length > 0 && (
                        <TableCell>
                          <Select
                            value={booking.group_id ?? undefined}
                            onValueChange={(groupId) => reassign.mutate({ ticketId: booking.id, groupId, previousGroupId: booking.group_id })}
                            disabled={reassign.isPending}
                          >
                            <SelectTrigger className="h-8 w-40"><SelectValue placeholder="No wave" /></SelectTrigger>
                            <SelectContent>
                              {groups.map((g) => (
                                <SelectItem key={g.id} value={g.id}>{g.name} · {formatGroupTime(g.start_time, tz)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Interest List */}
          {interestList && interestList.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4" />
                <h4 className="font-semibold">Interest List ({interestList.length} users)</h4>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Notified</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {interestList.map((interest: any) => (
                    <TableRow key={interest.id}>
                      <TableCell className="font-medium">
                        {`${interest.profiles?.first_name || ''} ${interest.profiles?.last_name || ''}`.trim() || 'Unknown'}
                      </TableCell>
                      <TableCell>{interest.profiles?.email || 'N/A'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(interest.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                      </TableCell>
                      <TableCell>
                        {interest.notified_at ? (
                          <span className="text-sm text-success">
                            {new Date(interest.notified_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not yet</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminEventBookings;